import { useQuery } from "@tanstack/react-query";
import { userService } from "@/api/services";
import { useAuthStore } from "@/stores/auth";
import { usernameFromName } from "@/api/resource";
import { qk } from "@/lib/query";
import { useEffect } from "react";
import { queryClient } from "@/lib/query";
import { onRealtimeEvent } from "@/lib/realtime";

/**
 * Unread notification count for the nav badge. Updates on list changes and on
 * realtime comment/mention events (FE-INBOX-05).
 */
export function useUnreadCount(): number {
  const user = useAuthStore((s) => s.user);
  const username = user ? usernameFromName(user.name) : "";

  const { data } = useQuery({
    queryKey: qk.notifications(username, "unread-count"),
    queryFn: async () => {
      const res = await userService.listNotifications(username, {
        filter: 'status == "UNREAD"',
        pageSize: 1000,
      });
      return (res.notifications ?? []).length;
    },
    enabled: Boolean(username),
    staleTime: 30_000,
  });

  // Refresh the count when realtime events arrive (new comments/mentions).
  useEffect(() => {
    if (!username) return;
    return onRealtimeEvent((e) => {
      if (e.type === "memo.comment.created") {
        queryClient.invalidateQueries({ queryKey: qk.notifications(username, "unread-count") });
      }
    });
  }, [username]);

  return data ?? 0;
}
